// Typed key constants for the `site_branding` key/value table.
// Add new keys here AND seed them in a migration so the admin UI has something
// to edit on first render.

export const BRANDING_TEXT_KEYS = [
  "identity.name",
  "identity.tagline",
  "identity.phone",
  "identity.email",
  "identity.address",
  "identity.hours",

  "color.primary",
  "color.accent",
  "color.background",
  "color.foreground",
  "color.muted",
  "color.destructive",

  "logo.light",
  "logo.dark",
  "logo.favicon",
  "logo.favicon_16",
  "logo.favicon_32",
  "logo.favicon_180",

  "hero.headline",
  "hero.subheadline",
  "hero.cta_text",
  "hero.image_url",

  "footer.legal_text",
  "footer.social_facebook",
  "footer.social_instagram",
] as const;

export const BRANDING_JSON_KEYS = [
  "typography.heading",
  "typography.body",
] as const;

export type BrandingTextKey = (typeof BRANDING_TEXT_KEYS)[number];
export type BrandingJsonKey = (typeof BRANDING_JSON_KEYS)[number];
export type BrandingKey = BrandingTextKey | BrandingJsonKey;

export interface FontPairing {
  family: string;
  weights: string[];
}

// Curated typography pairings. Shown in the admin Typography tab as a
// radio-style list — no open Google Fonts picker (decision fatigue for a
// non-designer owner). Owner picks one of these; we apply both heading + body
// in a single click.
export interface TypographyPreset {
  id: string;
  label: string;
  description: string;
  heading: FontPairing;
  body: FontPairing;
}

export const TYPOGRAPHY_PRESETS: TypographyPreset[] = [
  {
    id: "classic-elegant",
    label: "Classic Elegant",
    description: "Serif display headings, clean sans body. The current default.",
    heading: { family: "DM Serif Display", weights: ["400"] },
    body: { family: "Inter", weights: ["400", "500", "700"] },
  },
  {
    id: "modern-warm",
    label: "Modern Warm",
    description: "Geometric serif headings with a friendly humanist body.",
    heading: { family: "Fraunces", weights: ["400", "600"] },
    body: { family: "DM Sans", weights: ["400", "500", "700"] },
  },
  {
    id: "refined-editorial",
    label: "Refined Editorial",
    description: "High-contrast serif headings paired with a workhorse sans.",
    heading: { family: "Playfair Display", weights: ["400", "600"] },
    body: { family: "Source Sans 3", weights: ["400", "600"] },
  },
  {
    id: "friendly-approachable",
    label: "Friendly & Approachable",
    description: "Warm rounded headings, soft body — casual catering brands.",
    heading: { family: "Raleway", weights: ["600", "800"] },
    body: { family: "Nunito", weights: ["400", "600"] },
  },
  {
    id: "bold-statement",
    label: "Bold Statement",
    description: "Heavy sans display for impact; compact body for density.",
    heading: { family: "Archivo", weights: ["700", "900"] },
    body: { family: "Inter", weights: ["400", "500"] },
  },
  {
    id: "timeless-traditional",
    label: "Timeless Traditional",
    description: "Formal serif pairing — weddings, fine-dining contexts.",
    heading: { family: "Cormorant Garamond", weights: ["500", "700"] },
    body: { family: "Lato", weights: ["400", "700"] },
  },
  {
    id: "soft-contemporary",
    label: "Soft Contemporary",
    description: "Modern serif body with a light sans heading.",
    heading: { family: "Work Sans", weights: ["500", "700"] },
    body: { family: "Libre Caslon Text", weights: ["400", "700"] },
  },
  {
    id: "artisan-handcrafted",
    label: "Artisan Handcrafted",
    description: "Softly drawn serif headings with a humanist body.",
    heading: { family: "Young Serif", weights: ["400"] },
    body: { family: "Figtree", weights: ["400", "600"] },
  },
];

// Curated color palettes — shown as clickable swatches in the Colors tab.
// Each is a starting point; owner can fine-tune the primary/accent afterwards.
export interface ColorPalette {
  id: string;
  label: string;
  description: string;
  values: {
    primary: string;
    accent: string;
    background: string;
    foreground: string;
    muted: string;
    destructive: string;
  };
}

export const COLOR_PALETTES: ColorPalette[] = [
  {
    id: "sage-gold",
    label: "Sage & Gold",
    description: "Tristano's current — dark sage with warm gold accent.",
    values: {
      primary: "129 26% 22%",
      accent: "42 78% 60%",
      background: "0 0% 100%",
      foreground: "129 26% 12%",
      muted: "129 10% 94%",
      destructive: "0 72% 51%",
    },
  },
  {
    id: "midnight-copper",
    label: "Midnight & Copper",
    description: "Deep navy with burnished copper — upscale, evening-event feel.",
    values: {
      primary: "218 45% 18%",
      accent: "20 70% 52%",
      background: "0 0% 100%",
      foreground: "218 45% 10%",
      muted: "218 10% 94%",
      destructive: "0 72% 51%",
    },
  },
  {
    id: "olive-cream",
    label: "Olive & Cream",
    description: "Warm olive with cream neutrals — rustic / farm-to-table.",
    values: {
      primary: "80 30% 30%",
      accent: "35 65% 58%",
      background: "40 30% 98%",
      foreground: "80 30% 14%",
      muted: "40 15% 92%",
      destructive: "0 72% 51%",
    },
  },
  {
    id: "burgundy-blush",
    label: "Burgundy & Blush",
    description: "Romantic burgundy with dusty rose — wedding-forward.",
    values: {
      primary: "345 45% 30%",
      accent: "10 55% 72%",
      background: "0 0% 100%",
      foreground: "345 30% 15%",
      muted: "345 10% 95%",
      destructive: "0 72% 51%",
    },
  },
  {
    id: "charcoal-saffron",
    label: "Charcoal & Saffron",
    description: "Modern charcoal with saffron accent — corporate / live kitchen.",
    values: {
      primary: "220 10% 20%",
      accent: "38 85% 55%",
      background: "0 0% 100%",
      foreground: "220 15% 10%",
      muted: "220 5% 94%",
      destructive: "0 72% 51%",
    },
  },
  {
    id: "forest-cream",
    label: "Forest & Cream",
    description: "Deep forest green with cream — classic, timeless.",
    values: {
      primary: "140 40% 22%",
      accent: "42 55% 55%",
      background: "40 25% 98%",
      foreground: "140 35% 10%",
      muted: "40 15% 93%",
      destructive: "0 72% 51%",
    },
  },
];
